import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import CardImage from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/components/CardImage/CardImage';
import TimedBadgeContainer from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/TimedBadgeContainer/TimedBadgeContainer';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics';
import {
    ItemCardSource,
    useTrackItemCardClicked,
} from '@/utils/amplitude/analytics/item/trackItemCardClicked.analytics';

import { ItemPageItemCardProps } from '../ItemPageItemCard';

const MOBILE_IMAGE_QUALITY = 70;
const DESKTOP_IMAGE_QUALITY = 80;

const MOBILE_DIMENSION = 181;
const DESKTOP_DIMENSION = 240;

export const ImageComponent = createImage<ItemPageItemCardProps>({
    ImageComponent: ({
        className,
        isSold,
        isTimedPlusAuction,
        itemCardSource = ItemCardSource.LotDetailsPage,
        itemId,
        position,
        promoted,
        recommendationType,
    }) => {
        const { formatMessage } = useIntl();

        const trackItemCardClicked = useTrackItemCardClicked();
        const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
        const onRecommendationClick = useRecommendationTracking({
            isZeroBasedPosition: true,
            itemId,
            recommendationPosition: position ?? 0,
            recommendationType,
        });

        const handleClick = useCallback(
            (trigger: LotRecommendationTrigger) => {
                onRecommendationClick(trigger);
                trackItemCardClicked({
                    catalogId,
                    itemId,
                    promoted: Boolean(promoted),
                    source: itemCardSource,
                });
            },
            [catalogId, itemCardSource, itemId, promoted, trackItemCardClicked, onRecommendationClick]
        );

        const isMobile = useIsMobileLayout();
        // 1240 = caterwaul's old theme.breakpoints.pageWidthNum — no hammer-ui token matches exactly.
        const useSmallerImageQuality = !useIsWiderThan(1240);

        const handleMouseHover = (isHovered: boolean) => {
            const itemCard = document.getElementById(`card-${itemId}`);
            if (itemCard) {
                if (isHovered) {
                    itemCard.classList.add('image-hover');
                } else {
                    itemCard.classList.remove('image-hover');
                }
            }
        };

        return (
            <>
                {isTimedPlusAuction && <TimedBadgeContainer itemId={itemId} />}
                {!isSold && promoted && !isTimedPlusAuction && (
                    <Badge
                        className="absolute top-xs left-xs z-[1]"
                        color="neutral"
                        round
                    >
                        {formatMessage({ id: 'featured' })}
                    </Badge>
                )}
                {!isTimedPlusAuction && isSold && (
                    <Badge
                        className="absolute top-xs left-xs z-[1]"
                        color="neutral"
                        emphasis="bold"
                        size="sm"
                    >
                        {formatMessage({ id: 'similarSoldItemCarousel.badge' })}
                    </Badge>
                )}
                <div className={cn('overflow-hidden rounded-md border border-border-tertiary', className)}>
                    <CardImage
                        applyDimensionToUrl
                        canShowEasyShipping
                        dimension={useSmallerImageQuality ? MOBILE_DIMENSION : DESKTOP_DIMENSION}
                        enterLinkHoverState={() => handleMouseHover(true)}
                        imageQuality={useSmallerImageQuality ? MOBILE_IMAGE_QUALITY : DESKTOP_IMAGE_QUALITY}
                        isLabeledFavoriteButton={false}
                        isMobile={isMobile}
                        itemId={itemId}
                        lazyLoadImage
                        leaveLinkHoverState={() => handleMouseHover(false)}
                        onNavigate={() => {
                            handleClick(LotRecommendationTrigger.ImageClick);
                        }}
                        sharpenImage
                    />
                </div>
            </>
        );
    },
});
